import type {
  CreateOptions,
  FirebaseConfig,
  InitOptions,
} from '../services/interfaces';

/**
 * Minimal document contract used by waitForDOMReady().
 * Enables deterministic tests without mutating global document state.
 */
export interface DOMReadyTarget {
  readyState: DocumentReadyState;
  addEventListener(
    type: 'DOMContentLoaded',
    listener: () => void,
    options?: AddEventListenerOptions | boolean
  ): void;
}

export interface ResolvedCreateOptions {
  initOptions: InitOptions;
  element: string | HTMLElement | null | undefined;
}

/**
 * Resolve create() inputs into InitOptions used by the service layer.
 * Enforces that callers choose exactly one mode: sceneId or config.
 */
export function resolveCreateOptions(
  options: CreateOptions,
  defaultFirebaseConfig: FirebaseConfig
): ResolvedCreateOptions {
  const sceneIdRaw = 'sceneId' in options ? options.sceneId : undefined;
  const sceneId = typeof sceneIdRaw === 'string' ? sceneIdRaw.trim() : '';
  const hasSceneId = sceneId.length > 0;
  const hasConfig = 'config' in options && options.config != null;

  if (hasSceneId === hasConfig) {
    throw new Error('XRGallery.create() requires exactly one of sceneId or config');
  }

  const initOptions: InitOptions = {};

  if (hasSceneId) {
    const firebaseConfig = 'firebaseConfig' in options ? options.firebaseConfig : undefined;
    initOptions.firebaseConfig = firebaseConfig ?? defaultFirebaseConfig;
    initOptions.sequenceId = sceneId;
  } else {
    initOptions.config = options.config;
  }

  if (options.title) {
    initOptions.title = options.title;
  }

  return {
    initOptions,
    element: options.element,
  };
}

/**
 * Wait for DOMContentLoaded when the DOM is still parsing.
 */
export async function waitForDOMReady(target: DOMReadyTarget = document): Promise<void> {
  if (target.readyState !== 'loading') {
    return;
  }

  await new Promise<void>((resolve) => {
    target.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });
}

